import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import './App.css'
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom'

import Auth0ProviderWithHistory from './providers/Auth0ProviderWithHistory'
import { AppUpdateProvider } from './providers/AppUpdateContext'
import { AppLoadingProvider } from './providers/AppLoadingContext'
import { AuthStateProvider } from './providers/AuthStateContext'
import { NotificationProvider } from './providers/NotificationContext'
import { AdminAccessProvider } from './providers/AdminAccessContext'

import AppLayout from './providers/AppLayout'
import AdminShell from './components/AdminShell/AdminShell.component'

import Home from './routes/Home/Home.route'
import HospitalSearch from './routes/Hospitals/HospitalSearch/HospitalSearch.route'
import AgencySearch from './routes/Agencies/AgencySearch/AgencySearch.route'
import Login from './routes/Login/Login.route'
import Signup from './routes/Signup/Signup.route'
import AuthRedirect from './routes/AuthRedirect/AuthRedirect.route'
import Logout from './routes/Logout/Logout.route'
import AccessDenied from './routes/AccessDenied/AccessDenied.route'
import HospitalDetail from './routes/Hospitals/HospitalDetail/HospitalDetail.route'
import ReportedReviewsDashboard from './routes/ReportedReviews/ReportedReviewsDashboard.route'
import ReportedReviewRedirect from './routes/ReportedReviews/ReportedReviewRedirect.route'
import Notifications from './routes/Notifications/Notifications.route'
import AgencyDetail from './routes/Agencies/AgencyDetail/AgencyDetail.route'
import NotFound from './routes/NotFound/NotFound.route'
import ClaimPagesDashboard from './routes/ClaimPages/HospitalClaimPagesDashboard.route'
import NewOrganizationRequests from './routes/NewOrganization/NewOrganizationRequestDashboard'
import AdminPageCustomizations from './routes/Admin/PageCustomizations/AdminPageCustomizations.route'
import AdminTeam from './routes/Admin/Team/AdminTeam.route'

const AppLayoutRoute = (): JSX.Element => (
  <AppLayout>
    <Outlet />
  </AppLayout>
)

function App() {
  return (
    <BrowserRouter>
      <Auth0ProviderWithHistory>
        <AppUpdateProvider>
          <AppLoadingProvider>
            <AuthStateProvider>
              <NotificationProvider>
                <AdminAccessProvider>
                  <Routes>
                    <Route path="/access-denied" element={<AccessDenied />} />
                    <Route path="/unauthorized" element={<Navigate to="/access-denied" replace />} />
                    <Route path="/login/redirect/:redirect" element={<Login />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup/redirect/:redirect" element={<Signup />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/logout" element={<Logout />} />
                    <Route path="/redirect" element={<AuthRedirect />} />
                    <Route path="/404" element={<NotFound />} />

                    <Route element={<AdminShell />}>
                      <Route element={<AppLayoutRoute />}>
                        <Route path="/" element={<Home />} />
                        <Route path="/admin/team" element={<AdminTeam />} />
                        <Route path="/admin/reviews" element={<ReportedReviewsDashboard />} />
                        <Route path="/admin/reviews/reported-reviews" element={<ReportedReviewsDashboard />} />
                        <Route path="/admin/reviews/accepted-reviews" element={<ReportedReviewsDashboard />} />
                        <Route path="/admin/reviews/rejected-reviews" element={<ReportedReviewsDashboard />} />
                        <Route path="/admin/reported-reviews/:reviewId" element={<ReportedReviewRedirect />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/admin/claim-pages" element={<ClaimPagesDashboard />} />
                        <Route path="/admin/page-customizations" element={<AdminPageCustomizations />} />
                        <Route path="/admin/claim-pages/claimed-pages" element={<ClaimPagesDashboard />} />
                        <Route path="/admin/claim-pages/accepted-claimed-pages" element={<ClaimPagesDashboard />} />
                        <Route path="/admin/claim-pages/rejected-claimed-pages" element={<ClaimPagesDashboard />} />
                        <Route path="/admin/claim-pages/withdrawn-claimed-pages" element={<ClaimPagesDashboard />} />
                        <Route path="/admin/hospital-requests" element={<NewOrganizationRequests />} />
                        <Route path="/admin/agency-requests" element={<NewOrganizationRequests />} />
                        <Route path="/hospitals" element={<HospitalSearch />} />
                        <Route path="/hospitals/:id" element={<HospitalDetail />} />
                        <Route path="/agencies" element={<AgencySearch />} />
                        <Route path="/agencies/:id" element={<AgencyDetail />} />
                        <Route
                          path="*"
                          element={
                            <Navigate to="/404" state={{ originalUrl: window.location.pathname }} replace />
                          }
                        />
                      </Route>
                    </Route>

                    <Route
                      path="*"
                      element={<Navigate to="/404" state={{ originalUrl: window.location.pathname }} replace />}
                    />
                  </Routes>
                </AdminAccessProvider>
              </NotificationProvider>
            </AuthStateProvider>
          </AppLoadingProvider>
        </AppUpdateProvider>
      </Auth0ProviderWithHistory>
    </BrowserRouter>
  )
}

export default App
